import React, { useState, useEffect } from 'react';
import { Layers, X, Calendar, RefreshCw, Link as LinkIcon, Hash } from 'lucide-react';
import { apiService } from '../api/apiclient';
import { PLATFORMS_CONFIG, globalStore } from "../configs/constant";

const PostDetailPanel = ({ postId, onClose }) => {
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [accounts, setAccounts] = useState([]);


    useEffect(() => {
        if (!postId) {
            setPost(null);
            return;
        }

        const fetchPostDetail = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await apiService.getPostDetail(postId);
                setPost(response.post || response);
            } catch (err) {
                console.error("Failed to load post detail:", err);
                setError("Failed to load details. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchPostDetail();

        const fetchAccounts = async () => {
            try {
                if (globalStore.accounts.length > 0 && !globalStore.isDirty) {
                    setAccounts(globalStore.accounts);
                } else {
                    setLoading(true);
                    const response = await apiService.listAccounts();
                    const data = response.accounts || [];
                    setAccounts(data);
                    globalStore.accounts = data;
                    globalStore.isDirty = false;
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchAccounts();
    }, [postId]);

    const parseSafeJSON = (str, fallback = []) => {
        try { return typeof str === 'string' ? JSON.parse(str) : (str || fallback); }
        catch (e) { return fallback; }
    };
    // 1. 空状态：没有选择任何帖子
    if (!postId) {
        return (
            <div className="h-full w-full flex flex-col items-center justify-center text-gray-400 p-8">
                <Layers size={48} className="text-gray-200 mb-3 animate-pulse" />
                <p className="text-sm font-medium">Select a post from the list to view comprehensive distribution details.</p>
            </div>
        );
    }

    // 2. 加载状态
    if (loading) {
        return (
            <div className="h-full w-full flex flex-col items-center justify-center text-gray-400 p-8">
                <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-sm font-medium text-gray-500">Fetching live metadata...</p>
            </div>
        );
    }

    // 3. 接口报错状态
    if (error) {
        return (
            <div className="h-full w-full flex flex-col items-center justify-center text-red-400 p-8">
                <p className="text-sm font-bold mb-2">{error}</p>
                <button
                    onClick={() => setPost(null)}
                    className="text-xs text-red-600 underline font-semibold"
                >
                    Clear Selection
                </button>
            </div>
        );
    }

    // 4. 数据成功加载后的完整主面板
    if (!post) return null;

    console.log(post);
    const mediaUrls = post.media_urls || [];
    const platforms = parseSafeJSON(post.platforms);
    const accountIds = parseSafeJSON(post.account_ids);
    const tags = parseSafeJSON(post.tags);

    return (
        <div className="h-full w-full bg-white rounded-2xl p-6 md:p-8 overflow-y-auto custom-scrollbar flex flex-col gap-6 animate-in slide-in-from-right duration-200 relative">

            {/* 移动端专属关闭按钮（如果是从父组件传进来的 onClose） */}
            {onClose && (
                <button
                    onClick={onClose}
                    className="xl:hidden absolute top-4 right-4 z-50 p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition"
                >
                    <X size={18} className="text-gray-600" />
                </button>
            )}

            {/* 1. 标题与核心状态微标 */}
            <div className="border-b border-gray-100 pb-4 pr-8">
                <div className="flex flex-wrap gap-2 mb-3">
                    <span className={`px-2.5 py-1 text-xs font-bold uppercase rounded-md tracking-wider border ${post.status === 'published' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                        {post.status}
                    </span>
                    {post.media_type && (
                        <span className="px-2.5 py-1 text-xs font-bold bg-red-50 text-red-700 border border-red-100 uppercase rounded-md">
                            {post.media_type}
                        </span>
                    )}
                </div>
                <h2 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight leading-snug">
                    {post.title || "Post"}
                </h2>
            </div>

            {/* 2. 核心文案文本与 Hash 标签 */}
            <div className="bg-slate-50/50 border border-slate-100 p-5 rounded-2xl">
                <p className="text-gray-700 text-sm md:text-base leading-relaxed whitespace-pre-wrap selection:bg-red-100">
                    {post.content || <span className="text-gray-400 italic">No text content provided.</span>}
                </p>

                {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4 pt-3 border-t border-slate-100">
                        {tags.map((tag, idx) => (
                            <span key={idx} className="inline-flex items-center text-xs font-semibold text-red-600 hover:underline cursor-pointer">
                                <Hash size={12} />{tag.replace('#', '')}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* 3. 媒体矩阵：智能兼容 9:16 短视频和图片宫格 */}
            {mediaUrls.length > 0 && (
                <div className="space-y-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Attached Media ({mediaUrls.length})</h4>

                    {post.media_type === 'video' || mediaUrls[0]?.endsWith('.mp4') ? (
                        <div className="rounded-2xl overflow-hidden bg-black max-w-sm border border-gray-100 shadow-inner">
                            <video src={mediaUrls[0]} controls className="w-full aspect-[9/16] object-contain max-h-[450px]" />
                        </div>
                    ) : (
                        <div className={`grid gap-2 ${mediaUrls.length === 1 ? 'grid-cols-1' : mediaUrls.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                            {mediaUrls.map((url, idx) => (
                                <div key={idx} className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-100 shadow-sm">
                                    <img src={url} alt={`Media ${idx}`} className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* 4. 分发雷达：目标平台和账号 ID */}
            {/* 目标平台 */}
            <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Target Platforms</h4>
                {platforms.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {platforms.map((platform, idx) => {
                            const pConfig = PLATFORMS_CONFIG[platform.toLowerCase()] || { label: platform, color: 'bg-gray-50 text-gray-700 border-gray-200', icon: '🌐' };
                            return (
                                <span key={idx} className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-xl text-xs font-bold transition-all shadow-sm ${pConfig.color}`}>
                                    <i className={pConfig.icon}></i>
                                    {pConfig.label}
                                </span>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-xs text-gray-400 italic">No platforms targeted.</p>
                )}
            </div>

            {/* 连接的账号 */}
            <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Connected Accounts</h4>
                {accountIds.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {accountIds.map((accId) => {
                            const fullAccount = globalStore.accounts.find(
                                (acc) => String(acc.id) === String(accId)
                            );
                            console.log(fullAccount);
                            const accountName = fullAccount?.account_name;
                            const platform = fullAccount?.platform?.toLowerCase() || 'unknown';

                            const pConfig = PLATFORMS_CONFIG[platform] || { icon: '👤', color: 'bg-slate-50 text-slate-700 border-slate-200' };

                            return (
                                <span
                                    key={accId}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-xl text-xs font-bold transition-all shadow-sm ${pConfig.color}`}
                                    title={`ID: ${accId}`} // 鼠标悬停时依然能看到完整 ID，兼顾开发调试
                                >
                                    {/* 平台小图标 (如 🎥, 🎵) */}
                                    <i className={pConfig.icon}></i>

                                    {/* 账号昵称/名字 */}
                                    <span className="max-w-[120px] truncate">{accountName}</span>
                                </span>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-xs text-gray-400 italic">No accounts linked.</p>
                )}
            </div>


            {/* 5. 转化追踪：引流跳转链接 */}
            {post.target_link && (
                <div className="space-y-2 border-t border-gray-100 pt-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Target Action Link</h4>
                    <a href={post.target_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-red-600 hover:underline bg-red-50/40 px-3 py-2 rounded-xl border border-red-50 max-w-full truncate">
                        <LinkIcon size={14} className="shrink-0" />
                        <span className="truncate">{post.target_link}</span>
                    </a>
                </div>
            )}

            {/* 6. 审计日志足迹 */}
            <div className="mt-auto pt-6 border-t border-gray-100 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-gray-400">
                {post.scheduled_at && (
                    <div className="flex items-center gap-1.5">
                        <Calendar size={13} className="text-amber-500" />
                        <span>Scheduled: {new Date(post.scheduled_at).toLocaleString()}</span>
                    </div>
                )}
                <div className="flex items-center gap-1.5">
                    <RefreshCw size={13} />
                    <span>Updated: {new Date(post.updated_at || post.created_at).toLocaleString()}</span>
                </div>
            </div>

            {/* 7. 操作：一键克隆发帖 */}
            <div className="pt-2">
                <button className="w-full bg-red-600 text-white py-3.5 rounded-xl font-bold hover:bg-red-700 active:scale-[0.99] transition shadow-md hover:shadow-lg">
                    Clone to Repost
                </button>
            </div>

        </div>
    );
};

export default PostDetailPanel;